<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-15
-->

<script lang="ts" setup>
import { ref, watch, type PropType } from 'vue';

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  alt: {
    type: String,
    default: '',
  },
  imgClass: {
    type: String,
    default: '',
  },
  loaderSize: {
    type: String,
    default: 'h-16 w-16',
  },
  loaderText: {
    type: String,
    default: 'Loading...',
  },
  loaderOverlayClass: {
    type: String,
    default: 'bg-slate-900/90',
  },
  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    default: 'lazy',
  },
});

const emit = defineEmits<{
  (e: 'load' | 'error'): void;
}>();

const isLoaded = ref(false);
const hasError = ref(false);

watch(
  () => props.src,
  () => {
    isLoaded.value = false;
    hasError.value = false;
  }
);

const onLoad = () => {
  isLoaded.value = true;
  emit('load');
};

const onError = () => {
  hasError.value = true;
  emit('error');
};
</script>

<template>
  <div class="relative h-full w-full">
    <img
      :src="src"
      :alt="alt"
      :class="[imgClass, isLoaded ? 'opacity-100' : 'opacity-0']"
      class="transition-opacity duration-300"
      :loading="loading"
      @load="onLoad"
      @error="onError"
    />

    <!-- Logo loader component -->
    <Transition name="fade-loader">
      <LogoLoader
        v-if="!isLoaded && !hasError"
        :size="loaderSize"
        :text="loaderText"
        :overlay-class="loaderOverlayClass"
      />
    </Transition>
  </div>
</template>

<style scoped>
.fade-loader-enter-active,
.fade-loader-leave-active {
  transition: opacity 0.5s ease;
}

.fade-loader-enter-from,
.fade-loader-leave-to {
  opacity: 0;
}
</style>
